import * as React from 'react';
import { PrimaryButton } from '@fluentui/react/lib/Button';
import { DatePicker } from '@fluentui/react/lib/DatePicker';
import { Dropdown, IDropdownOption } from '@fluentui/react/lib/Dropdown';
import { MessageBar, MessageBarType } from '@fluentui/react/lib/MessageBar';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { Stack } from '@fluentui/react/lib/Stack';
import { TextField } from '@fluentui/react/lib/TextField';
import { IProject } from '../../api/model/IProject';
import { Task } from '../../api/Task';
import { TimePicker } from '@fluentui/react/lib/TimePicker';
import { acquireResourceId } from '../../service/resource.service';
import { isAlldayEvent, getWorkDays } from "../../api/GraphProvider";
import { DayOfWeek } from '../../api/model/IWorkingHours';
import { showMessage } from '../../service/globalState.service';
import { checkPendingCategory } from '../../service/settingsState.service';
import { getEventId } from '../../service/appointment.service';


const { useState, useEffect } = React;

interface CreateTaskProps {
  onCreateSuccess: () => void;
  projects: IProject[];
}

interface Message {
  type: MessageBarType;
  text: string;
  show: boolean;
}

export const CreateTask: React.FC<CreateTaskProps> = ({ onCreateSuccess, projects }) => {
  const [name, setName] = useState<string | undefined>();
  const [date, setDate] = useState<Date>();
  const [time, setTime] = useState<Date>(new Date());
  const [work, setWork] = useState<string | undefined>('');
  const [spinner, showSpinner] = useState<boolean>(false);
  const [message, setMessage] = useState<Message | undefined>();
  const [isMessageActive, setMessageActive] = useState<Message | undefined>();
  const [selectedProject, setSelectedProject] = useState<IProject>();


  useEffect(() => {
    fetchResource();
    parseTimes();
  }, [])

  useEffect(() => {
    const { item } = Office.context.mailbox;
    item.subject.getAsync(result => parseSubject(result.value));
  }, [projects])

  const resourceId = React.useRef<string>();
  const fetchResource = async () => {
    resourceId.current = await acquireResourceId();
  }

  const parseSubject = (subject: string) => {
    if (!subject) return;

    const [projectName, taskName] = subject.split(/ -(.*)/);
    const foundProject = projects.find(p => p.Name.toLowerCase() === projectName.trim().toLowerCase());
    if (!foundProject) {
      setName(subject);
      return;
    }

    setSelectedProject(foundProject);
    if (taskName)
      setName(taskName.trim());
  }

  const checkIsAllDay = async (start: Date, end: Date): Promise<boolean> => {
    try {
      return await isAlldayEvent(await getEventId())
    }
    catch {
      return start.toTimeString().includes("00:00:00") && end.toTimeString().includes("00:00:00")
    }
  }

  const parseTimes = () => {
    const { item } = Office.context.mailbox;

    if (!item.start) return;

    item.start.getAsync(res => {
      const start = res.value;
      console.log(start);
      if (!start) return;
      setDate(new Date(start));
      setTime(new Date(start));
      item.end.getAsync(async r => {
        const end = r.value;
        if (!end) return;

        const msDiff = Math.abs(end.getTime() - start.getTime());
        const hours = msDiff / 36e5;
        const roundedHours = Math.round((hours + Number.EPSILON) * 100) / 100
        const isAllDay = await checkIsAllDay(start, end);
        if (!isAllDay)
          setWork(String(roundedHours))
        else {
          const tempDate = new Date(start);
          const weekday: DayOfWeek[] = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
          const workDays = await getWorkDays();
          let countedDays = 0;
          for (let i = 0; i < Math.ceil(msDiff / 864e5); i++) {
            if (workDays.daysOfWeek.includes(weekday[tempDate.getDay()])) {
              countedDays++
            }
            tempDate.setDate(tempDate.getDate() + 1)
          }
          setWork(String(countedDays * 8))
        }
      })
    })
  }

  const projectOptions: IDropdownOption<IProject>[] = projects.map(p => ({
    key: p.Id,
    text: p.Name,
    data: p
  }));

  const create = async () => {
    showSpinner(true);

    try {
      const workHours = Math.abs(Number(work));
      const workTotal = Math.round(workHours * 60);
      const z = new Date().getTimezoneOffset() * 60 * 1000;

      const startDate = new Date(time.getTime() - z);
      startDate.setFullYear(date.getFullYear(), date.getMonth(), date.getDate());



      const taskId = await Task.create({
        Name: name,
        ProjectId: selectedProject.Id,
        StartDate: startDate.toISOString(),
        ResourceId: resourceId.current,
        Work: workTotal
      });


      const successMessage = {
        type: MessageBarType.success,
        text: `Task ${name} in project ${selectedProject.Name} successfully created!`,
        show: true
      };
      setMessage(successMessage);
      showMessage(successMessage);
      resetState();
      onCreateSuccess();
      const applyCategory = checkPendingCategory();
      if(applyCategory) {
        const { item } = Office.context.mailbox;
        item.categories.addAsync(["Pending"]);
        item.body.prependAsync(`
          <p>
          You have already requested an Allocatus Task for this event. Here some details about your request:<br/>
            <br/>
            Request date: ${new Date().toLocaleString()}<br/>
            Project: ${selectedProject.Name}<br/>
            Duration: ${workTotal / 60}h<br/>
          </p>
          `,
          { coercionType: Office.CoercionType.Html }
        )
      }
    } catch (e) {
      setMessage({
        type: MessageBarType.error,
        text: JSON.stringify(e),
        show: true
      })
    }

    showSpinner(false);
  }

  const resetState = () => {
    setName('');
    setDate(new Date());
    setTime(new Date());
    setWork('');
  }

  console.log('>>>>>TIME: ', time)
  return (
    <>
      {message && message.show && (
        <MessageBar
          messageBarType={message.type}
          onDismiss={(e) => {
            setMessage({ ...message, show: false })
        }}
        >
          {message.text}
        </MessageBar>
      )}

      <TextField
        label="Name"
        required
        value={name}
        onChange={(e, v) => setName(v)}
      />

      <Dropdown
        label="Project"
        required
        options={projectOptions}
        selectedKey={selectedProject?.Id}
        onChange={(e, v) => setSelectedProject(v.data)}
      />

      <DatePicker
        label="Start Date"
        isRequired
        value={date || new Date()}
        onSelectDate={(v) => {

          console.log(v.toISOString().slice(0, 10));
          setDate(v)

        }}
      />

      <Stack horizontal tokens={{ childrenGap: 10 }}>
        {time && <TimePicker
          label='Start Time'
          required
          increments={30}
          useHour12
          dropdownWidth={100}
          value={time}
          onChange={(e, v) => setTime(v)}
        />}

        <TextField
          label="Work"
          suffix="Hours"
          required
          type="number"
          value={work}
          onChange={(e, v) => setWork(v)}
        />
      </Stack>

      <br />

      <Stack
        horizontal
        verticalAlign="center"
        tokens={{ childrenGap: 20 }}
      >
        <PrimaryButton
          text="Create"
          disabled={!(name && selectedProject && work)}
          onClick={create}
        />

        {spinner && <Spinner size={SpinnerSize.small} />}
      </Stack>
    </>
  );
}